import { useState, forwardRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Perimenopause / Menopause Care",
  "Intimate & Vulvar Health",
  "Labiaplasty & Corrective Procedures",
  "General Consultation",
];

const fieldClass =
  "w-full bg-transparent border-b border-[var(--astera-char)]/20 py-3 text-[var(--astera-char)] placeholder:text-[var(--astera-char)]/35 focus:outline-none focus:border-clay transition-colors duration-300";

export const Contact = forwardRef((_, ref) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request received — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="bg-char text-[var(--astera-bone)] py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        {/* Info + map */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[var(--astera-olive-light)]">
              <span className="h-[1px] w-10 bg-[var(--astera-olive-light)]" /> Visit ASTERA
            </p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight">
              Begin with a <span className="italic text-clay">conversation.</span>
            </h2>
            <p className="mt-6 text-[var(--astera-bone)]/60 leading-relaxed max-w-md">
              Share a little about what you're experiencing. A member of our care
              team will reach out to find a time that works for you.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-5">
              {[
                { icon: MapPin, label: "7982 Yonge St, Innisfil, ON L9S 1L5" },
                { icon: Phone, label: "Text or Call (249) 388-2997" },
                { icon: Phone, label: "Text or Call (705) 985-7295" },
                { icon: Mail, label: "hello@asterawomenshealth.ca" },
              ].map((it, i) => (
                <div key={i} className="flex items-center gap-4">
                  <it.icon size={18} className="text-clay shrink-0" />
                  <span className="text-sm text-[var(--astera-bone)]/75">{it.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 overflow-hidden rounded-[22px] border border-white/10 h-56">
              <iframe
                title="ASTERA Women's Health location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=7982+Yonge+St+Innisfil+ON+L9S+1L5&output=embed"
                className="w-full h-full grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="appointment-form"
              className="rounded-[28px] bg-bone text-[var(--astera-char)] p-8 lg:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[var(--astera-char)]/50">Full name</label>
                  <input
                    data-testid="form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[var(--astera-char)]/50">Email</label>
                  <input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[var(--astera-char)]/50">Phone (optional)</label>
                  <input
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(___) ___-____"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[var(--astera-char)]/50">I'm interested in</label>
                  <select
                    data-testid="form-interest"
                    value={form.interest}
                    onChange={update("interest")}
                    className={`${fieldClass} cursor-pointer`}
                  >
                    <option value="">Select a focus…</option>
                    {INTERESTS.map((it) => (
                      <option key={it} value={it}>{it}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.16em] text-[var(--astera-char)]/50">How can we help?</label>
                  <textarea
                    data-testid="form-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell us a little about what you're experiencing…"
                    className={`${fieldClass} resize-none`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[var(--astera-char)] px-8 py-4 text-sm uppercase tracking-[0.16em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-clay disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request Appointment
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-5 text-xs text-[var(--astera-char)]/45">
                Your details are kept private and used only to arrange your care.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";
